import React, { useEffect, Fragment } from "react";
import { compose } from "recompose";
import { connect } from "react-redux";
import { createStructuredSelector } from "reselect";
import { withRouter, useRouter } from "next/router";
import { withFormik } from "formik";
import * as yup from "yup";
import cn from "classnames";
import { getDocumentsHost } from "common/helper";
import { useFormattedMessage, useSideModalScrollToTop } from "hooks";
import PropertyItem from "components/PropertyItem";
import Input from "components/Input";
import DownloadButton from "components/DownloadButton";
import Checkbox from "components/Checkbox";
import CustomFormatNumber from "components/CustomFormatNumber";
import PrevButton from "components/PrevButton";
import NextButton from "components/NextButton";
import { selectTenant } from "store/app/selectors";
import { selectProjectDetails } from "store/project/selectors";
import {
    selectInvestmentStepData,
    selectIsInvestmentCreated,
    selectIsInvestmentLoading,
    selectIsInvestmentFailed,
} from "store/user/investment/selectors";
import { createInvestMentDataStart } from "store/user/investment/actions";
import {
    selectUserDashboardDetails,
    selectEmail,
    selectIsTanganyCreateWalletConfirmed,
    selectUserDetails,
} from "store/user/account/selectors";
import { somethingWrongText } from "i18n/messages/errors";
import PDFString from "constants/project_pdf_info";
import { nextButtonText } from "i18n/messages/buttonTitles";
import { selectCurrenKYCStatus, selectKycFormData, selectKycProviderData } from "store/user/kyc/selectors";
import { KYC_STATUS_LOCAL } from "constants/status";
import { TENANTS_LIST } from "constants/index";
import { checkGTM } from "utils";

const DOCUMENT_HOST = getDocumentsHost();

const messages = {
    investmentOverviewCheckboxValidationRequired: "agree_to_invest",
    heading: "overview",
    personalData: "personal_data",
    paymentMethod: "payment_method",
    transfer: "investment_overview_transfer",
    withdrawalAccount: "withdrawal_accounts",
    yourInvestment: "continue_investment_heading",
    investmentAmount: "investment_overview_amount",
    promotionCode: "promotion_code",
    statutoryRiskWarningTitle: "statutory_risk_warning_title",
    statutoryRiskWarningContent: "statutory_risk_warning_content",
    paymentMethods: "payment_methods",
    noPaymentMethodRequired: "no_payment_method_required",
    noPaymentMethodInvestmentInfo: "no_payment_method_investment_info",
    contractDocumentsHeading: "contract_documents_heading",
    contractDocumentsConten1: "contract_documents_listed",
    contractDocumentsConten2: "understand_all_contract",
    drawingSlip: "contract_incl_revocation_instruction",
    risks: "risks",
    risksContent: "before_completing_investnment",
    risksCheckbox: "take_note_of_risks",
    costsAndGrants: "costs_and_grants",
    costsAndGrantsCheckbox: "costs_and_grants_checkbox",
    basicInformationSheet: "basic_information_sheet",
    basicInformationSheetCheckbox: "read_basic_information_sheet",
    basicInformationSheetContent: "right_request_a_paper_copy",
    termsBond: "terms_of_the_bond",
    termsBondCheckbox: "read_noted_terms_conditions",
    effectaTermsConditions: "effecta_gmbh_terms_conditions",
    effectaTermsConditionsCheckbox: "read_general_terma_conditions",
    effectaConsumerInformation: "accept_bo_effecta_cipdf_text",
    effectaConsumerInformationCheckbox: "read_consumer_information_effecta",
    dataForwarding: "data_forwarding",
    dataForwardingCheckbox: "safe_keeping_condition_checkbox2",
    confirmationRequired: "your_confirmation_is_required",
    herebyConsent: "i_hereby_consent",
    confirmationPoint1: "my_info_is_complete_correct",
    confirmationPoint2: "on_my_own_account",
    confirmationPoint3: "recording_telephone_calls",
    confirmationPoint4: "provide_evidence",
    confirmationPoint5: "transferred_to_liability_umbrella",
    buttonText: "buy_now_text",
    loadingButtonText: "please_wait_button",
    cashlinkTncHeading: "cashlink_tnc_heading",
    cashlinkTncCheckbox: "cashlink_tnc_checkbox",
    cashlinkPdfTitle: "cashlink_pdf_title",
    tanganyTncHeading: "tangany_tnc_heading",
    tanganyTncCheckbox: "tangany_tnc_checkbox",
    tanganyPdfTitle: "tangany_pdf_title",
    gfkTncHeading: "gfk_tnc_heading",
    gfkTncCheckbox: "gfk_tnc_checkbox",
    gfkPdfTitle: "gfk_pdf_title",
    reverseSolicitationHeading: "reverse_solicitation_heading",
    reverseSolicitationSubHeading: "reverse_solicitation_subheading",
    reverseSolicitationCheckbox: "reverse_solicitation_checkbox",
};

const InvestmentOverviewData = ({
    className,
    setInvestmentStepData,
    investmentStepData,
    setFieldValue,
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    handleSubmit,
    validateForm,
    projectData = [],
    isInvestmentCreated,
    isInvestmentLoading,
    backSlideEffect = "",
    setBackSlideEffect,
    investmentError,
    isGfkOnboardingProduct = false,
    isEwPgInvestmentProcess = false,
    IsTanganyCreateWalletConfirmed,
    userKYCData,
    userData,
    userKycProviderData,
    productCatalog,
    productType,
}) => {
    useSideModalScrollToTop();

    const router = useRouter();

    const investmentDocuments =
        investmentStepData && investmentStepData.investmentData && investmentStepData.investmentData.Documents;

    useEffect(() => {
        validateForm();
    }, []);

    useEffect(() => {
        if (isInvestmentCreated === true) {
            setInvestmentStepData({ overviewData: { step: "investmentSuccess", data: values } });
            setBackSlideEffect("step-slide-left");
        }
    }, [isInvestmentCreated]);
    const personalData = userKYCData?.personal;
    const investSum = investmentStepData.investSum;

    const userNationality = userKycProviderData?.Nationality || userData?.Identification?.Nationality || "";
    const showReverseSolicitationCheckbox = userNationality?.toLowerCase() !== "de";

    const { formatMessage } = useFormattedMessage();

    const fetchedProjects = projectData;
    let project = null;
    if (router.query && router.query.id && fetchedProjects && Array.isArray(fetchedProjects) && fetchedProjects.length) {
        project = fetchedProjects.find((item) => item.projectID === router.query.id);
    }

    function isDocumentExists(fileName) {
        return investmentDocuments && investmentDocuments.find((document) => document.Filename.includes(fileName));
    }

    const PdfComponent = ({ heading, documentName = "", subHeading, documentTitle, documentInfo, checkbox, extra }) => {
        const file = documentName && isDocumentExists(documentName);

        return (
            <div className="bg-grey-full mt-10 document-box">
                {(heading || subHeading) && (
                    <div className="upper-part">
                        {heading && <span className="text-14 fw-600 d-block mb-2">{heading}</span>}
                        {subHeading && <p>{subHeading}</p>}
                    </div>
                )}
                <div className="bottom-part">
                    {checkbox &&
                        checkbox.length > 0 &&
                        checkbox.map(({ name, option, value, setFieldValue, errorMessage, error, p }, index) => (
                            <Fragment key={index}>
                                <div className="check-section bg-grey-full flex space-between">
                                    <Checkbox
                                        inputId={option}
                                        name={name}
                                        value={value}
                                        label={p}
                                        onChange={() => {
                                            setFieldValue(name, !value);
                                        }}
                                        isError={error}
                                    />
                                </div>
                                {error && (
                                    <div className="error-element">
                                        <label className="error-label text-left">{errorMessage}</label>
                                    </div>
                                )}
                            </Fragment>
                        ))}
                    {extra && <span className="text-14 mb-7 d-block font-italic">{extra}</span>}

                    {file && (
                        <DownloadButton
                            isToken
                            fileURL={`${DOCUMENT_HOST}/download${file.Path}${file.Filename}`}
                            fileName={file.Filename}
                            title={documentTitle}
                            description={documentInfo}
                        />
                    )}
                </div>
            </div>
        );
    };

    const projectID = project && project.projectID;

    return (
        <>
            <form method="post" onSubmit={handleSubmit}>
                <div className={`${className} investment-overview-data ${backSlideEffect}`}>
                    <h3>{formatMessage(messages.heading)}</h3>
                    <div className="personal-data-overview single-overview">
                        <div className="flex space-between header-part">
                            <h4 className="mt-0">{formatMessage(messages.personalData)}</h4>
                        </div>
                        <div className="border-x mt-0" />
                        <div className="content-part">
                            <span className="text-14 fw-500 d-block mb-2">
                                {personalData.FirstName} {personalData.LastName}
                            </span>
                            <span className="text-14 fw-500 d-block mb-2">
                                {personalData.Street} {personalData.Streetnumber}
                            </span>
                            <span className="text-14 fw-500 d-block mb-2">
                                {personalData.Zipcode} {personalData.City}
                            </span>
                            <span className="text-14 fw-500 d-block mb-2">{personalData.Email}</span>
                            <span className="text-14 fw-500 d-block mb-2">{`+${personalData.DialCode} ${personalData.Number}`}</span>
                        </div>
                    </div>
                    <div className="your-investment-overview single-overview">
                        <div className="flex space-between header-part">
                            <h4 className="mt-0">{formatMessage(messages.yourInvestment)}</h4>
                        </div>
                        <div className="border-x mt-0" />
                        <div className="content-part">
                            <PropertyItem
                                className="cursor-unset"
                                tileHoverAnimation={false}
                                image={
                                    project && project.images && project.images.teaserImage
                                        ? `${project.images.teaserImage.path}?w=345`
                                        : null
                                }
                                propertyType={productType}
                                propertyCategory={productCatalog}
                                title={project && project.title}
                                location={`${project && project.address && project.address.city}, ${
                                    project && project.address && project.address.country
                                }`}
                                digitalShares={investSum && investSum.digitalShares}
                                aggregate={investSum && investSum.digitalShares * 1}
                                returnEstimate={project && project.financials && project.financials.totalReturnEstimate}
                                status={project && project.status && project.status.status}
                                price={1}
                                isVideo={project?.images?.teaserVideo?.path ? true : false}
                                videoPath={project?.images?.teaserVideo?.path}
                                isNotLink
                            />
                            <div className="flex space-between bg-grey-full investment-amount single-overview">
                                <h4 className="my-0">{formatMessage(messages.investmentAmount)}</h4>
                                <span className="text-18 fw-600">
                                    <CustomFormatNumber value={(investSum && investSum.sum) || 0} />
                                </span>
                            </div>
                        </div>
                    </div>
                    <div className="code-overview single-overview">
                        <div className="content-part">
                            <Input
                                label={formatMessage(messages.promotionCode)}
                                type="text"
                                name="CouponCode"
                                placeholder=""
                                onChange={handleChange}
                                value={values.CouponCode}
                                onBlur={handleBlur}
                            />
                        </div>
                    </div>
                    <div className="risk-warning single-overview bg-grey-full">
                        <div className="content-part">
                            <span className="text-14 fw-600 d-block mb-2">
                                {formatMessage(messages.statutoryRiskWarningTitle)}
                            </span>
                            <p>{formatMessage(messages.statutoryRiskWarningContent)}</p>
                        </div>
                    </div>
                    <div className="single-overview">
                        <div className="flex space-between header-part">
                            <h4 className="mt-0">{formatMessage(messages.paymentMethods)}</h4>
                        </div>
                        <div className="border-x mt-0" />
                        <span className="text-14 fw-600 d-block mb-2">{formatMessage(messages.noPaymentMethodRequired)}</span>
                        <p>{formatMessage(messages.noPaymentMethodInvestmentInfo)}</p>
                    </div>
                    <div className="border-x mt-0" />
                    <div className="contract-documents single-overview">
                        <div className="flex space-between header-part">
                            <h4 className="mt-0">{formatMessage(messages.contractDocumentsHeading)}</h4>
                        </div>
                        <div className="border-x mt-0" />
                        <div className="content-part">
                            <p>{formatMessage(messages.contractDocumentsConten1)}</p>
                            <p>{formatMessage(messages.contractDocumentsConten2)}</p>
                            <PdfComponent
                                heading={formatMessage(messages.drawingSlip)}
                                documentName="Zeichnungsschein.pdf"
                                documentTitle="Zeichnungsschein inkl. Widerrufsbelehrung"
                                documentInfo={
                                    (PDFString[projectID] && PDFString[projectID]["ZeichnungsscheinMenu"]) ||
                                    "Stand 10/2024 - PDF - 964 KB"
                                }
                            />
                            <PdfComponent
                                heading={formatMessage(messages.risks)}
                                subHeading={formatMessage(messages.risksContent)}
                                checkbox={[
                                    {
                                        name: "IsRisksConfirmed",
                                        option: "IsRisksConfirmed",
                                        value: values.IsRisksConfirmed,
                                        setFieldValue: setFieldValue,
                                        errorMessage: errors.IsRisksConfirmed
                                            ? formatMessage(messages[errors.IsRisksConfirmed])
                                            : null,
                                        error: touched.IsRisksConfirmed && errors.IsRisksConfirmed,
                                        p: formatMessage(messages.risksCheckbox),
                                    },
                                ]}
                                documentName="Risiken.pdf"
                                documentTitle="Risiken inkl. Interessenkonflikt"
                                documentInfo={
                                    (PDFString[projectID] && PDFString[projectID]["RisikenMenu"]) ||
                                    "Stand 10/2024 - PDF - 122 KB"
                                }
                            />
                            <PdfComponent
                                heading={formatMessage(messages.costsAndGrants)}
                                checkbox={[
                                    {
                                        name: "IsCostConfirmed",
                                        option: "IsCostConfirmed",
                                        value: values.IsCostConfirmed,
                                        setFieldValue: setFieldValue,
                                        errorMessage: errors.IsCostConfirmed
                                            ? formatMessage(messages[errors.IsCostConfirmed])
                                            : null,
                                        error: touched.IsCostConfirmed && errors.IsCostConfirmed,
                                        p: formatMessage(messages.costsAndGrantsCheckbox),
                                    },
                                ]}
                                documentName="Effecta_Kosten_und_Zuwendungen.pdf"
                                documentTitle="Kosten und Zuwendungen"
                                documentInfo={
                                    (PDFString[projectID] && PDFString[projectID]["EffectaKostenundZuwendungenMenu"]) ||
                                    "Stand 10/2024 - PDF - 68 KB"
                                }
                            />
                            <PdfComponent
                                heading={formatMessage(messages.basicInformationSheet)}
                                checkbox={[
                                    {
                                        name: "IsBIBConfirmed",
                                        option: "IsBIBConfirmed",
                                        value: values.IsBIBConfirmed,
                                        setFieldValue: setFieldValue,
                                        errorMessage: errors.IsBIBConfirmed
                                            ? formatMessage(messages[errors.IsBIBConfirmed])
                                            : null,
                                        error: touched.IsBIBConfirmed && errors.IsBIBConfirmed,
                                        p: formatMessage(messages.basicInformationSheetCheckbox),
                                    },
                                ]}
                                documentName="Basisinformationsblatt.pdf"
                                documentTitle="Basisinformationsblatt"
                                documentInfo={
                                    (PDFString[projectID] && PDFString[projectID]["BasisinformationsblattMenu"]) ||
                                    "Stand 10/2024 - PDF - 164 KB"
                                }
                                extra={formatMessage(messages.basicInformationSheetContent)}
                            />
                            <PdfComponent
                                heading={formatMessage(messages.termsBond)}
                                checkbox={[
                                    {
                                        name: "IsBondTermsConfirmed",
                                        option: "IsBondTermsConfirmed",
                                        value: values.IsBondTermsConfirmed,
                                        setFieldValue: setFieldValue,
                                        errorMessage: errors.IsBondTermsConfirmed
                                            ? formatMessage(messages[errors.IsBondTermsConfirmed])
                                            : null,
                                        error: touched.IsBondTermsConfirmed && errors.IsBondTermsConfirmed,
                                        p: formatMessage(messages.termsBondCheckbox),
                                    },
                                ]}
                                documentName="Schuldverschreibungsbedingungen.pdf"
                                documentTitle="Schuldverschreibungsbedingun&shy;gen"
                                documentInfo={
                                    (PDFString[projectID] && PDFString[projectID]["SchuldverschreibungsbedingungenMenu"]) ||
                                    "Stand 11/2024 - PDF - 228 KB"
                                }
                            />
                            <PdfComponent
                                heading={formatMessage(messages.effectaTermsConditions)}
                                checkbox={[
                                    {
                                        name: "IsBrokerageTermsConfirmed",
                                        option: "IsBrokerageTermsConfirmed",
                                        value: values.IsBrokerageTermsConfirmed,
                                        setFieldValue: setFieldValue,
                                        errorMessage: errors.IsBrokerageTermsConfirmed
                                            ? formatMessage(messages[errors.IsBrokerageTermsConfirmed])
                                            : null,
                                        error: touched.IsBrokerageTermsConfirmed && errors.IsBrokerageTermsConfirmed,
                                        p: formatMessage(messages.effectaTermsConditionsCheckbox),
                                    },
                                ]}
                                documentName="Effecta_AGB.pdf"
                                documentTitle="Effecta GmbH AGB"
                                documentInfo={
                                    (PDFString[projectID] && PDFString[projectID]["EffectaAGBMenu"]) ||
                                    "Stand 03/2023 - PDF - 133 KB"
                                }
                            />
                            <PdfComponent
                                heading={formatMessage(messages.effectaConsumerInformation)}
                                checkbox={[
                                    {
                                        name: "IsConsumerInformationConfirmed",
                                        option: "IsConsumerInformationConfirmed",
                                        value: values.IsConsumerInformationConfirmed,
                                        setFieldValue: setFieldValue,
                                        errorMessage: errors.IsConsumerInformationConfirmed
                                            ? formatMessage(messages[errors.IsConsumerInformationConfirmed])
                                            : null,
                                        error: touched.IsConsumerInformationConfirmed && errors.IsConsumerInformationConfirmed,
                                        p: formatMessage(messages.effectaConsumerInformationCheckbox),
                                    },
                                ]}
                                documentName="Effecta_Verbraucherinformationen.pdf"
                                documentTitle={formatMessage(messages.effectaConsumerInformation)}
                                documentInfo={
                                    (PDFString[projectID] && PDFString[projectID]["EffectaConsumerInformationMenu"]) ||
                                    "Stand 05/2022 - PDF - 132 KB"
                                }
                            />
                            {isGfkOnboardingProduct ? (
                                <PdfComponent
                                    heading={formatMessage(messages.gfkTncHeading)}
                                    checkbox={[
                                        {
                                            name: "IsCryptoSecuritiesInformationConfirmed",
                                            option: "IsCryptoSecuritiesInformationConfirmed",
                                            value: values.IsCryptoSecuritiesInformationConfirmed,
                                            setFieldValue: setFieldValue,
                                            errorMessage: errors.IsCryptoSecuritiesInformationConfirmed
                                                ? formatMessage(messages[errors.IsCryptoSecuritiesInformationConfirmed])
                                                : null,
                                            error:
                                                touched.IsCryptoSecuritiesInformationConfirmed &&
                                                errors.IsCryptoSecuritiesInformationConfirmed,
                                            p: formatMessage(messages.gfkTncCheckbox),
                                        },
                                    ]}
                                    documentName="GFK_AGB.pdf"
                                    documentTitle={formatMessage(messages.gfkPdfTitle)}
                                    documentInfo={
                                        (PDFString[projectID] && PDFString[projectID]["GFKAGBMenu"]) ||
                                        "Stand 10/2024 - PDF - 221 KB"
                                    }
                                />
                            ) : null}
                            {isEwPgInvestmentProcess ? (
                                <>
                                    <PdfComponent
                                        heading={formatMessage(messages.cashlinkTncHeading)}
                                        checkbox={[
                                            {
                                                name: "IsCryptoSecuritiesInformationConfirmed",
                                                option: "IsCryptoSecuritiesInformationConfirmed",
                                                value: values.IsCryptoSecuritiesInformationConfirmed,
                                                setFieldValue: setFieldValue,
                                                errorMessage: errors.IsCryptoSecuritiesInformationConfirmed
                                                    ? formatMessage(messages[errors.IsCryptoSecuritiesInformationConfirmed])
                                                    : null,
                                                error:
                                                    touched.IsCryptoSecuritiesInformationConfirmed &&
                                                    errors.IsCryptoSecuritiesInformationConfirmed,
                                                p: formatMessage(messages.cashlinkTncCheckbox),
                                            },
                                        ]}
                                        documentName="Cashlink_AGB.pdf"
                                        documentTitle={formatMessage(messages.cashlinkPdfTitle)}
                                        documentInfo={
                                            (PDFString[projectID] && PDFString[projectID]["CashlinkAGBMenu"]) ||
                                            "Stand 02/2024 - PDF - 149 KB"
                                        }
                                    />
                                    <PdfComponent
                                        heading={formatMessage(messages.tanganyTncHeading)}
                                        checkbox={[
                                            {
                                                name: "IsCryptoCustodianInformationConfirmed",
                                                option: "IsCryptoCustodianInformationConfirmed",
                                                value: values.IsCryptoCustodianInformationConfirmed,
                                                setFieldValue: setFieldValue,
                                                errorMessage: errors.IsCryptoCustodianInformationConfirmed
                                                    ? formatMessage(messages[errors.IsCryptoCustodianInformationConfirmed])
                                                    : null,
                                                error:
                                                    touched.IsCryptoCustodianInformationConfirmed &&
                                                    errors.IsCryptoCustodianInformationConfirmed,
                                                p: formatMessage(messages.tanganyTncCheckbox),
                                            },
                                        ]}
                                        documentName="Tangany_AGB.pdf"
                                        documentTitle={formatMessage(messages.tanganyPdfTitle)}
                                        documentInfo={
                                            (PDFString[projectID] && PDFString[projectID]["TanganyMenu"]) ||
                                            "Stand 06/2022 - PDF - 223 KB"
                                        }
                                    />
                                </>
                            ) : null}
                            {showReverseSolicitationCheckbox && (
                                <PdfComponent
                                    heading={formatMessage(messages.reverseSolicitationHeading)}
                                    subHeading={formatMessage(messages.reverseSolicitationSubHeading)}
                                    checkbox={[
                                        {
                                            name: "IsReverseSolicitation",
                                            option: "IsReverseSolicitation",
                                            value: values.IsReverseSolicitation,
                                            setFieldValue: setFieldValue,
                                            errorMessage: errors.IsReverseSolicitation
                                                ? formatMessage(messages[errors.IsReverseSolicitation])
                                                : null,
                                            error: touched.IsReverseSolicitation && errors.IsReverseSolicitation,
                                            p: formatMessage(messages.reverseSolicitationCheckbox),
                                        },
                                    ]}
                                />
                            )}
                            <PdfComponent
                                heading={formatMessage(messages.dataForwarding)}
                                checkbox={[
                                    {
                                        name: "IsCustodyDataForwardingConfirmed",
                                        option: "IsCustodyDataForwardingConfirmed",
                                        value: values.IsCustodyDataForwardingConfirmed,
                                        setFieldValue: setFieldValue,
                                        errorMessage: errors.IsCustodyDataForwardingConfirmed
                                            ? formatMessage(messages[errors.IsCustodyDataForwardingConfirmed])
                                            : null,
                                        error:
                                            touched.IsCustodyDataForwardingConfirmed && errors.IsCustodyDataForwardingConfirmed,
                                        p: formatMessage(messages.dataForwardingCheckbox),
                                    },
                                ]}
                            />
                            <div className="single-overview personal-information pb-2">
                                <div className="flex space-between header-part">
                                    <h4>{formatMessage(messages.confirmationRequired)}</h4>
                                </div>
                                <div className="border-x mt-0" />
                                <div className="content-part">
                                    <div className="check-section">
                                        <div className="flex align-center">
                                            <Checkbox
                                                inputId="IsPersonalInformationConfirmed"
                                                name="IsPersonalInformationConfirmed"
                                                value={values.IsPersonalInformationConfirmed}
                                                label={formatMessage(messages.herebyConsent)}
                                                labelClassName="fw-600"
                                                onChange={() => {
                                                    setFieldValue(
                                                        "IsPersonalInformationConfirmed",
                                                        !values.IsPersonalInformationConfirmed,
                                                    );
                                                }}
                                                isError={
                                                    touched.IsPersonalInformationConfirmed &&
                                                    errors.IsPersonalInformationConfirmed
                                                }
                                            />
                                        </div>
                                        <ul className="mt-4 mb-0">
                                            <li>{formatMessage(messages.confirmationPoint1)}</li>
                                            <li>{formatMessage(messages.confirmationPoint2)}</li>
                                            <li>{formatMessage(messages.confirmationPoint3)}</li>
                                            <li>{formatMessage(messages.confirmationPoint4)}</li>
                                            <li>{formatMessage(messages.confirmationPoint5)}</li>
                                        </ul>
                                    </div>
                                    {touched.IsPersonalInformationConfirmed && errors.IsPersonalInformationConfirmed && (
                                        <div className="error-element">
                                            <label className="error-label text-left">
                                                {formatMessage(messages[errors.IsPersonalInformationConfirmed])}
                                            </label>
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="border-x my-10" />
                        </div>
                    </div>
                </div>
            </form>
            {investmentError && (
                <div className="error-element">
                    <label className="error-label text-left">{formatMessage(somethingWrongText)}</label>
                </div>
            )}
            <div className="bottom-bar-container">
                <PrevButton
                    onClick={() => {
                        setInvestmentStepData({ investmentData: { step: "investmentCalculationStep" } });
                        setBackSlideEffect("step-slide-right");
                    }}
                />
                <NextButton
                    className={cn({
                        "mw-fit": !isEwPgInvestmentProcess || (isEwPgInvestmentProcess && IsTanganyCreateWalletConfirmed),
                        "disable-grey": Object.keys(errors).length,
                        "disable-pointer": isInvestmentLoading,
                        "create-investment-button": true,
                    })}
                    title={
                        isEwPgInvestmentProcess && !IsTanganyCreateWalletConfirmed
                            ? formatMessage(nextButtonText)
                            : isInvestmentLoading
                              ? formatMessage(messages.loadingButtonText)
                              : formatMessage(messages.buttonText)
                    }
                    onClick={handleSubmit}
                    isLoading={isInvestmentLoading}
                />
            </div>
        </>
    );
};

const mapStateToProps = createStructuredSelector({
    investmentStepData: selectInvestmentStepData,
    userDashboardDetails: selectUserDashboardDetails,
    isInvestmentCreated: selectIsInvestmentCreated,
    isInvestmentLoading: selectIsInvestmentLoading,
    investmentError: selectIsInvestmentFailed,
    projectData: selectProjectDetails,
    userEmail: selectEmail,
    currentTenant: selectTenant,
    IsTanganyCreateWalletConfirmed: selectIsTanganyCreateWalletConfirmed,
    currentKycStatus: selectCurrenKYCStatus,
    userKYCData: selectKycFormData,
    userData: selectUserDetails,
    userKycProviderData: selectKycProviderData,
});

const mapDispatchToProps = (dispatch) => ({
    createInvestMentDataStart: (data) => dispatch(createInvestMentDataStart(data)),
});

export default compose(
    withRouter,
    connect(mapStateToProps, mapDispatchToProps),
    withFormik({
        mapPropsToValues: (props) => {
            let overviewData = undefined;
            if (props.investmentStepData && props.investmentStepData.investmentData.overviewData)
                overviewData = props.investmentStepData.investmentData.overviewData.data;

            const userNationality = props.userKycProviderData?.Nationality || props.userData?.Identification?.Nationality || "";
            const showReverseSolicitationCheckbox = userNationality?.toLowerCase() !== "de";

            return (
                overviewData || {
                    CouponCode: "",
                    IsRisksConfirmed: false,
                    IsCostConfirmed: false,
                    IsBIBConfirmed: false,
                    IsBondTermsConfirmed: false,
                    IsBrokerageTermsConfirmed: false,
                    IsConsumerInformationConfirmed: false,
                    IsCustodyDataForwardingConfirmed: false,
                    IsPersonalInformationConfirmed: false,
                    ...(props.isGfkOnboardingProduct && {
                        IsCryptoSecuritiesInformationConfirmed: false,
                    }),
                    ...(props.isEwPgInvestmentProcess && {
                        IsCryptoSecuritiesInformationConfirmed: false,
                        IsCryptoCustodianInformationConfirmed: false,
                    }),
                    ...(showReverseSolicitationCheckbox && {
                        IsReverseSolicitation: false,
                    }),
                }
            );
        },
        validateOnBlur: true,
        validateOnChange: true,
        validationSchema: (props) => {
            const userNationality = props.userKycProviderData?.Nationality || props.userData?.Identification?.Nationality || "";
            const showReverseSolicitationCheckbox = userNationality?.toLowerCase() !== "de";

            return yup.object().shape({
                IsRisksConfirmed: yup
                    .bool()
                    .required("investmentOverviewCheckboxValidationRequired")
                    .oneOf([true], "investmentOverviewCheckboxValidationRequired"),
                IsCostConfirmed: yup
                    .bool()
                    .required("investmentOverviewCheckboxValidationRequired")
                    .oneOf([true], "investmentOverviewCheckboxValidationRequired"),
                IsBIBConfirmed: yup
                    .bool()
                    .required("investmentOverviewCheckboxValidationRequired")
                    .oneOf([true], "investmentOverviewCheckboxValidationRequired"),
                IsBondTermsConfirmed: yup
                    .bool()
                    .required("investmentOverviewCheckboxValidationRequired")
                    .oneOf([true], "investmentOverviewCheckboxValidationRequired"),
                IsBrokerageTermsConfirmed: yup
                    .bool()
                    .required("investmentOverviewCheckboxValidationRequired")
                    .oneOf([true], "investmentOverviewCheckboxValidationRequired"),
                IsConsumerInformationConfirmed: yup
                    .bool()
                    .required("investmentOverviewCheckboxValidationRequired")
                    .oneOf([true], "investmentOverviewCheckboxValidationRequired"),
                IsCustodyDataForwardingConfirmed: yup
                    .bool()
                    .required("investmentOverviewCheckboxValidationRequired")
                    .oneOf([true], "investmentOverviewCheckboxValidationRequired"),
                IsPersonalInformationConfirmed: yup
                    .bool()
                    .required("investmentOverviewCheckboxValidationRequired")
                    .oneOf([true], "investmentOverviewCheckboxValidationRequired"),
                ...(props.isGfkOnboardingProduct && {
                    IsCryptoSecuritiesInformationConfirmed: yup
                        .bool()
                        .required("investmentOverviewCheckboxValidationRequired")
                        .oneOf([true], "investmentOverviewCheckboxValidationRequired"),
                }),
                ...(props.isEwPgInvestmentProcess && {
                    IsCryptoSecuritiesInformationConfirmed: yup
                        .bool()
                        .required("investmentOverviewCheckboxValidationRequired")
                        .oneOf([true], "investmentOverviewCheckboxValidationRequired"),
                    IsCryptoCustodianInformationConfirmed: yup
                        .bool()
                        .required("investmentOverviewCheckboxValidationRequired")
                        .oneOf([true], "investmentOverviewCheckboxValidationRequired"),
                }),
                ...(showReverseSolicitationCheckbox && {
                    IsReverseSolicitation: yup
                        .bool()
                        .required("investmentOverviewCheckboxValidationRequired")
                        .oneOf([true], "investmentOverviewCheckboxValidationRequired"),
                }),
            });
        },
        handleSubmit: async (values, { props, setSubmitting, setErrors }) => {
            setErrors({ requestError: undefined });
            setSubmitting(false);
            const {
                isEwPgInvestmentProcess,
                IsTanganyCreateWalletConfirmed,
                projectData,
                router,
                userEmail,
                investmentStepData,
                createInvestMentDataStart,
                setInvestmentStepData,
                setBackSlideEffect,
                currentTenant,
            } = props;
            const projectID = router.query?.id;
            const project = projectData?.find((item) => item.projectID === projectID);

            const prepareInvestmentData = () => ({
                ...values,
                UserEmail: userEmail,
                ProjectID: project.projectID,
                uid: investmentStepData.investmentData.investmentID,
                Amount: `${investmentStepData.investSum.sum}`,
                IsXPConfirmed: investmentStepData.investmentData.IsXPConfirmed,
                IsMissingXPConfirmed: investmentStepData.investmentData.IsMissingXPConfirmed,
            });

            if (isEwPgInvestmentProcess) {
                if (IsTanganyCreateWalletConfirmed) {
                    if (KYC_STATUS_LOCAL.ERROR !== props.currentKycStatus && project) {
                        if (currentTenant === TENANTS_LIST.SACHWERTINVEST) {
                            checkGTM();
                            window?.dataLayer?.push({ event: "swi_sucessfull_invest_process" });
                        }

                        const investmentData = prepareInvestmentData();
                        createInvestMentDataStart && createInvestMentDataStart({ investment: { ...investmentData } });
                    }
                } else {
                    setInvestmentStepData({ overviewData: { step: "investmentTanganyData", data: values } });
                    setBackSlideEffect("step-slide-left");
                }
            } else if (KYC_STATUS_LOCAL.ERROR !== props.currentKycStatus && project) {
                if (currentTenant === TENANTS_LIST.SACHWERTINVEST) {
                    checkGTM();
                    window?.dataLayer?.push({ event: "swi_sucessfull_invest_process" });
                }

                const investmentData = prepareInvestmentData();
                createInvestMentDataStart && createInvestMentDataStart({ investment: { ...investmentData } });
            }
        },
        displayName: "InvestmentOverviewData",
    }),
)(InvestmentOverviewData);
